import React, { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { motion } from "framer-motion";
import { FiArrowLeft, FiLock, FiUser, FiLogIn } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";
import { TextField } from "@mui/material";
import { employeeLogin } from "../../services/auth.service";
import { getFingerprint } from "../../helpers/fingerprint";

const EmployeeLogin = ({ onBack, theme }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showeyePassword, setShoweyePassword] = useState(false);
  const [formData, setFormData] = useState({
    employeeLoginId: "",
    password: "",
  });
  const [errors, setErrors] = useState({});

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const getTextFieldSx = (theme) => ({
    "& .MuiInputLabel-root": {
      color: theme === "dark" ? "#9ca3af" : undefined,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme === "dark" ? "#374151" : "#f9fafb",
      color: theme === "dark" ? "#f3f4f6" : "#111827",
      "& fieldset": {
        borderColor: theme === "dark" ? "#4b5563" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#3b82f6",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
        borderWidth: "2px",
      },
    },
    "& .MuiFormHelperText-root": {
      color: theme === "dark" ? "#ef4444" : undefined,
    },
  });

  const togglePasswordVisibility = () => {
    setShoweyePassword((prev) => !prev);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.employeeLoginId) newErrors.employeeLoginId = t("Login ID is required");
    if (!formData.password) newErrors.password = t("Password is required");
    return newErrors;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const fingerprint = await getFingerprint();
      const trimmedData = {
        employeeLoginId: formData.employeeLoginId.trim(),
        password: formData.password.trim(),
        fingerprint,
      };
      const result = await employeeLogin(trimmedData);
      if (result.status === 200 || result.status === 201) {
        toast.success(t("Logged in successfully!"));
        const { employee } = result.data;
        await localStorage.setItem("token", result.headers?.get("x-auth-token"));
        await localStorage.setItem("role", employee?.position);
        await localStorage.setItem("employeeID", employee.employeeID);
        navigate(`/${result?.data?.businessId}/dashboard`);
        window.location.reload();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t("Invalid Login ID or password"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      <form onSubmit={handleLogin}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerChildren}
          className="space-y-6"
        >
          <motion.div variants={fadeInUp}>
            <TextField
              fullWidth
              name="employeeLoginId"
              label={t("Login ID")}
              value={formData.employeeLoginId}
              onChange={handleChange}
              error={!!errors.employeeLoginId}
              helperText={errors.employeeLoginId}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <FiUser className="mr-2 text-gray-400" />
                ),
              }}
              sx={getTextFieldSx(theme)}
            />
          </motion.div>

          <motion.div variants={fadeInUp}>
            <TextField
              fullWidth
              name="password"
              type={showeyePassword ? "text" : "password"}
              label={t("Password")}
              value={formData.password}
              onChange={handleChange}
              error={!!errors.password}
              helperText={errors.password}
              variant="outlined"
              size="medium"
              InputProps={{
                startAdornment: (
                  <FiLock className="mr-2 text-gray-400" />
                ),
                endAdornment: (
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    type="button"
                    onClick={togglePasswordVisibility}
                    className={`p-1 ${theme === "dark" ? "text-gray-400 hover:text-blue-300" : "text-gray-400 hover:text-blue-600"}`}
                  >
                    {showeyePassword ? <FaEyeSlash /> : <FaEye />}
                  </motion.button>
                ),
              }}
              sx={getTextFieldSx(theme)}
            />
          </motion.div>

          <motion.button
            variants={fadeInUp}
            type='submit'
            disabled={loading || !formData.employeeLoginId || !formData.password}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 mt-4 ${loading || !formData.employeeLoginId || !formData.password
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
              }`}
          >
            {loading ? (
              <>
                <CgSpinner className="animate-spin text-2xl" />
                <span>{t("Logging in...")}</span>
              </>
            ) : (
              <>
                <div className="p-2 bg-white/20 rounded-lg">
                  <FiLogIn className="text-xl" />
                </div>
                <span>{t("Login As Employee")}</span>
              </>
            )}
          </motion.button>

          <motion.button
            variants={fadeInUp}
            type="button"
            onClick={onBack}
            whileHover={{ x: -5 }}
            className={`w-full mt-4 py-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 group ${theme === "dark" ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-900"
              }`}
          >
            <FiArrowLeft className="group-hover:translate-x-[-2px] transition-transform" />
            <span>{t("Back to Login")}</span>
          </motion.button>
        </motion.div>
      </form>
    </div>
  );
};

export default EmployeeLogin;
